/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import styled from "styled-components";

import * as React from "react";
import { Button, Typography15 } from "@slid/slid-ips";
import { useEffect, useState } from "react";
import { useImageNodeStore } from "../store/useImageNodeStore";
import { ReactComponent as DeleteIcon } from "../images/icons/delete.svg";
import { useTranslation } from "react-i18next";

type Props = Readonly<{
  "data-test-id"?: string;
  accept?: string;
  label: string;
  onChange: (files: FileList | null) => void;
}>;

export default function ImageFileInput({ accept, label, onChange, "data-test-id": dataTestId }: Props): JSX.Element {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const { isImageFileUploaded, setIsImageFileUploaded } = useImageNodeStore();
  const [fileName, setFileName] = React.useState<string>("");
  const { t } = useTranslation("EditorModal");

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files) {
      setIsImageFileUploaded(true);
      onChange(files);
      setFileName(files[0].name);
    }
  };

  const handleDelete = () => {
    setFileName("");
    setIsImageFileUploaded(false);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the input value
    }
  };

  useEffect(() => {
    return () => {
      setIsImageFileUploaded(false);
      setFileName("");
    };
  }, []);

  return (
    <div>
      {!isImageFileUploaded && (
        <InputLabel>
          <Button appearance="line" size="medium" text={t("image.uploadButton", { keySeparator: ".", ns: "EditorModal" })} callback={handleButtonClick} />
        </InputLabel>
      )}
      <Input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          handleImageUpload(e);
        }}
        data-test-id={dataTestId}
      />
      {isImageFileUploaded && fileName && (
        <SelectedFileInput>
          <Typography15 text={fileName} color="--gray17" />
          <DeleteIconWrapper onClick={handleDelete}>
            <DeleteIcon />
          </DeleteIconWrapper>
        </SelectedFileInput>
      )}
    </div>
  );
}

const InputLabel = styled.label`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  display: none;
`;

const SelectedFileInput = styled.div`
  display: flex;
  width: 100%;
  padding: 14px 16px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  background: var(--gray1);
`;

const DeleteIconWrapper = styled.div`
  cursor: pointer;
`;
